import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';
import { Router, ActivatedRoute } from '@angular/router';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, filter, take, timeout, takeUntil } from 'rxjs/operators';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { AdminDropdownConfigResponse, DocumentCompareDifference, DocumentCompareResponse, DocumentModelService } from '../../services/document-model.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { ModalService, ModalType, ModalSize } from '../../services/modal.service';
import { NotificationService } from '../../services/notification.service';
import { TruthManagement as TruthManagementService } from '../../services/truth-management';
import { PageHeaderComponent } from '../page-header/page-header.component';

interface DocumentModelRow {
  name: string;
  fieldCount: number;
  lastModifiedDate: Date | null;
  lastModifiedBy: string | null;
}

interface DocumentTypeOption {
  code: string;
  name: string;
}

interface CreateDocumentModalForm {
  clientName: string;
  documentModel: string;
  documentType: string;
  contextType: string;
  llmModel: string;
  llmTokenLimit: string;
  ocrMode: string;
  otherDetails: string;
  enableExternalMapping: boolean;
}

interface EditDocumentModalForm {
  clientName: string;
  documentModel: string;
  documentType: string;
  contextType: string;
  llmModel: string;
  llmTokenLimit: string;
  ocrMode: string;
  otherDetails: string;
  enableExternalMapping: boolean;
}

interface CompareJsonLine {
  text: string;
  changed: boolean;
  path: string;
}

@Component({
  selector: 'app-admin-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule, PageHeaderComponent],
  templateUrl: './admin-settings.html',
  styleUrls: ['./admin-settings.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSettingsComponent implements OnInit, OnDestroy {
  private readonly fallbackTokenLimits: number[] = [2048, 4096, 8192, 16384];
  private readonly destroy$ = new Subject<void>();

  activeTab: 'documents' | 'general' | 'users' = 'documents';
  isAdmin: boolean = false;
  isSuperUser: boolean = false;
  isLoadingUserRole: boolean = false;
  isAuthenticated: boolean = false;
  isLoadingDocuments: boolean = true;

  documentModels: DocumentModelRow[] = [];
  searchQuery: string = '';
  sortColumn: 'name' | 'fields' | 'modifiedDate' | 'modifiedBy' | null = 'modifiedDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  sortActive: boolean = false;

  // View Fields dropdown
  expandedModel: string | null = null;
  expandedFields: string[] = [];
  loadingFields: boolean = false;
  dropdownTop: number = 0;
  dropdownLeft: number = 0;

  // Download and Import tracking
  downloadingModel: string | null = null;
  importingModel: string | null = null;

  // Create model modal state
  isCreateModalOpen: boolean = false;
  isCreateModalLoading: boolean = false;
  isCreateSubmitting: boolean = false;
  createModalError: string = '';

  // Edit model modal state
  isEditModalOpen: boolean = false;
  isEditModalLoading: boolean = false;
  isEditSubmitting: boolean = false;
  editModalError: string = '';
  editingDocumentModel: string = '';
  private editModalInitialSnapshot: string = '';
  private editConfigRequestCompleted: boolean = false;
  private editDropdownRequestCompleted: boolean = false;

  // Compare with environment modal state
  // Backend environment names, lowest to highest; the API host token for prod is 'app'
  private static readonly ENV_HIERARCHY: string[] = ['dev', 'qa', 'uat', 'prod'];
  readonly currentEnv: string = AdminSettingsComponent.getCurrentEnv();
  readonly compareEnvOptions: string[] = AdminSettingsComponent.getOtherEnvs();
  isPushDialogOpen: boolean = false;
  isPushing: boolean = false;
  pushTargetEnv: string = '';
  isPullDialogOpen: boolean = false;
  isPulling: boolean = false;
  pullTargetEnv: string = '';
  isCompareModalOpen: boolean = false;
  isComparing: boolean = false;
  compareModel: string = '';
  compareTargetEnv: string = '';
  compareError: string = '';
  compareResult: DocumentCompareResponse | null = null;
  compareCurrentLines: CompareJsonLine[] = [];
  compareTargetLines: CompareJsonLine[] = [];

  clientList: string[] = [];
  llmModels: string[] = [];
  ocrModes: string[] = [];
  tokenLimits: number[] = [...this.fallbackTokenLimits];
  dropdownDefaults: { llmModel: string; ocrMode: string; llmTokenLimit: string } = {
    llmModel: '',
    ocrMode: '',
    llmTokenLimit: ''
  };
  private clientsRequestCompleted: boolean = false;
  private dropdownConfigRequestCompleted: boolean = false;



  sequencingDocumentTypes: DocumentTypeOption[] = [];
  isSequencingDocumentTypesLoading: boolean = false;
  private sequencingDocumentTypesSub?: Subscription;

  isCreateDocTypeDropdownOpen: boolean = false;
  isEditDocTypeDropdownOpen: boolean = false;
  createDocTypeSearch: string = '';
  editDocTypeSearch: string = '';

  createModalForm: CreateDocumentModalForm = this.getEmptyCreateModalForm();
  editModalForm: EditDocumentModalForm = this.getEmptyEditModalForm();
  
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('currentJsonPanel') currentJsonPanel?: ElementRef<HTMLElement>;
  @ViewChild('targetJsonPanel') targetJsonPanel?: ElementRef<HTMLElement>;

  constructor(
    public router: Router,
    public authService: AuthService,
    private documentModelService: DocumentModelService,
    private excelExportService: ExcelExportService,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer,
    public cdr: ChangeDetectorRef,
    private truthManagementService: TruthManagementService,
    private activatedRoute: ActivatedRoute,
    private clientCapability: ClientCapabilityService,
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.expandedModel && !target.closest('.action-btn-wrapper')) {
      this.expandedModel = null;
      this.expandedFields = [];
      this.cdr.markForCheck();
    }
  }

  @HostListener('window:scroll', [])
  @HostListener('window:resize', [])
  onWindowScrollOrResize(): void {
    if (this.expandedModel) {
      this.expandedModel = null;
      this.expandedFields = [];
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    // Check for search query parameter from navigation
    this.activatedRoute.queryParams
      .pipe(
        filter((params) => params['search'] !== undefined),
        take(1)
      )
      .subscribe((params) => {
        this.searchQuery = params['search'];
        this.cdr.markForCheck();
      });

    this.authService.userRole$
      .pipe(filter(role => role !== null), take(1))
      .subscribe(role => {
        this.isAdmin = role === 'admin' || role === 'superuser';
        this.isSuperUser = role === 'superuser';
        this.isLoadingUserRole = false;
        this.cdr.markForCheck();
      });
    
    // Subscribe to client initialization and changes, reload models on both
    this.authService.currentClient$
      .pipe(
        filter((client) => client !== null),
        takeUntil(this.destroy$)
      )
      .subscribe((client) => {
        // Force-refresh (bypass cache) so landing on this page — including every
        // navigation back to it, since the component re-inits — shows accurate data.
        this.loadDocumentModels(true);
      });
    
    this.checkAuthentication();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.sequencingDocumentTypesSub?.unsubscribe();
  }

  private loadDocumentModels(forceRefresh: boolean = false): void {
    this.isLoadingDocuments = true;

    // Get current clientId from AuthService
    const currentClient = this.authService.getCurrentClient();
    const clientId = currentClient?.client_id;
    const summaryRequest = forceRefresh
      ? this.documentModelService.refreshDocumentModelsSummary(clientId)
      : this.documentModelService.getDocumentModelsSummary(clientId);

    summaryRequest.subscribe({
      next: (response: any) => {
        const models = Array.isArray(response) ? response : (response?.data || response?.documents || response?.document_models || []);
        this.documentModels = models.map((doc: any) => {
          let lastModifiedDate: Date | null = null;
          if (doc.last_modified_at) {
            const dateStr = doc.last_modified_at.endsWith('Z') ? doc.last_modified_at : doc.last_modified_at + 'Z';
            const parsed = new Date(dateStr);
            lastModifiedDate = Number.isNaN(parsed.getTime()) ? null : parsed;
          }
          const rawModifiedBy = doc.last_modified_by;
          const lastModifiedBy = typeof rawModifiedBy === 'string' && rawModifiedBy.trim().length > 0
            ? rawModifiedBy
            : null;
          return {
            name: doc.document_model || '',
            fieldCount: doc.field_count || 0,
            lastModifiedDate,
            lastModifiedBy
          };
        });
        this.isLoadingDocuments = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingDocuments = false;
        this.cdr.markForCheck();
      }
    });
  }

  get filteredDocumentModels(): DocumentModelRow[] {
    let result = this.documentModels;
    if (this.searchQuery.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      result = result.filter(m => m.name.toLowerCase().includes(q));
    }
    if (this.sortColumn) {
      result = [...result].sort((a, b) => {
        let cmp = 0;
        switch (this.sortColumn) {
          case 'name': cmp = a.name.localeCompare(b.name); break;
          case 'fields': cmp = a.fieldCount - b.fieldCount; break;
          case 'modifiedDate': {
            const at = a.lastModifiedDate ? a.lastModifiedDate.getTime() : Number.POSITIVE_INFINITY;
            const bt = b.lastModifiedDate ? b.lastModifiedDate.getTime() : Number.POSITIVE_INFINITY;
            cmp = at - bt;
            break;
          }
          case 'modifiedBy': {
            const aStr = a.lastModifiedBy ?? '￿';
            const bStr = b.lastModifiedBy ?? '￿';
            cmp = aStr.localeCompare(bStr);
            break;
          }
        }
        return this.sortDirection === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }

  checkAuthentication(): void {
    const userEmail = sessionStorage.getItem('userEmail');
    this.isAuthenticated = !!userEmail;
    this.cdr.markForCheck();
  }

  setActiveTab(tab: 'documents' | 'general' | 'users'): void {
    if (tab === 'general' || tab === 'users') return;
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  private showTechnicalError(action: string): void {
    this.notificationService.showError(action, [
      { label: 'Reason', value: 'Technical error' }
    ]);
  }

  private isDuplicateDocumentModelCreateError(error: any): boolean {
    const candidates: string[] = [];

    const errorPayload = error?.error;
    if (typeof errorPayload === 'string') {
      candidates.push(errorPayload);
    } else if (errorPayload && typeof errorPayload === 'object') {
      candidates.push(errorPayload.message || '');
      candidates.push(errorPayload.detail || '');
    }

    candidates.push(error?.message || '');

    const combined = candidates
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return combined.includes('already exists') && combined.includes('document model');
  }

  addDocument(): void {
    this.isCreateModalOpen = true;
    this.isCreateSubmitting = false;
    this.createModalError = '';
    this.createModalForm.clientName = this.authService.getCurrentClient()?.client_name ?? '';
    this.loadCreateModalOptions();
    this.loadSequencingDocumentTypes();
    this.cdr.markForCheck();
  }

  closeCreateModal(): void {
    if (this.isCreateSubmitting) {
      return;
    }

    this.isCreateModalOpen = false;
    this.isCreateDocTypeDropdownOpen = false;
    this.createModalError = '';
    this.createModalForm = this.getEmptyCreateModalForm();
    this.cdr.markForCheck();
  }

  requestCloseCreateModal(): void {
    if (this.isCreateSubmitting) {
      return;
    }

    if (!this.hasUnsavedCreateModalChanges) {
      this.closeCreateModal();
      return;
    }

    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Unsaved Changes',
      message: 'Do you want to save changes?',
      confirmText: 'Yes',
      cancelText: 'No',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onConfirm: () => this.submitCreateDocumentModel(),
      onCancel: () => this.closeCreateModal()
    });
  }

  get hasUnsavedCreateModalChanges(): boolean {
    return !!(
      this.createModalForm.documentModel.trim() ||
      this.createModalForm.documentType.trim() ||
      this.createModalForm.contextType.trim() ||
      this.createModalForm.llmModel.trim() ||
      this.createModalForm.llmTokenLimit.trim() ||
      this.createModalForm.ocrMode.trim() ||
      this.createModalForm.otherDetails.trim() ||
      this.createModalForm.enableExternalMapping
    );
  }

  onCreateModalBackdropClick(): void {
    return;
  }

  get isOtherDetailsJsonInvalid(): boolean {
    if (!this.createModalForm.otherDetails.trim()) {
      return false;
    }

    try {
      const parsed = JSON.parse(this.createModalForm.otherDetails);
      return typeof parsed !== 'object' || parsed === null || Array.isArray(parsed);
    } catch {
      return true;
    }
  }

  get isCreateButtonDisabled(): boolean {
    // Document Type is optional — when left empty, document_type_id is simply
    // omitted from the create payload (the service drops empty-string fields).
    const requiredMissing = !this.createModalForm.clientName.trim() ||
      !this.createModalForm.documentModel.trim();
    return requiredMissing || this.isCreateSubmitting || this.isOtherDetailsJsonInvalid;
  }

  get llmModelPlaceholder(): string {
    return this.dropdownDefaults.llmModel ? `${this.dropdownDefaults.llmModel} (default)` : 'Select LLM model';
  }

  get llmTokenLimitPlaceholder(): string {
    return this.dropdownDefaults.llmTokenLimit ? `${this.dropdownDefaults.llmTokenLimit} (default)` : 'Select token limit';
  }

  get ocrModePlaceholder(): string {
    return this.dropdownDefaults.ocrMode ? `${this.dropdownDefaults.ocrMode} (default)` : 'Select OCR mode';
  }

  submitCreateDocumentModel(): void {
    if (this.isOtherDetailsJsonInvalid) {
      this.notificationService.showError('Invalid JSON format in Other Details.');
      return;
    }

    if (this.isCreateButtonDisabled) {
      this.notificationService.showError('Please fill in all required fields.');
      return;
    }

    this.isCreateSubmitting = true;
    this.createModalError = '';
    this.cdr.markForCheck();

    const payload = this.buildCreateDocumentPayload();
    this.documentModelService.createDocumentModelForm(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess('Document model created successfully.');
        this.isCreateSubmitting = false;
        this.closeCreateModal();
        this.loadDocumentModels(true);
      },
      error: (error) => {
        this.isCreateSubmitting = false;
        this.createModalError = '';
        if (this.isDuplicateDocumentModelCreateError(error)) {
          this.notificationService.showError('Failed to create document model', [
            { label: 'Reason', value: 'Document model already exists' }
          ]);
        } else {
          this.showTechnicalError('Failed to create document model');
        }
        this.cdr.markForCheck();
      }
    });
  }

  private loadCreateModalOptions(): void {
    let createLoadErrorNotified = false;
    const notifyCreateLoadError = () => {
      if (createLoadErrorNotified) {
        return;
      }
      createLoadErrorNotified = true;
      this.showTechnicalError('Failed to load dropdowns (Clients, OCR Mode, LLM Model)');
    };

    this.isCreateModalLoading = true;
    this.createModalError = '';
    this.clientsRequestCompleted = false;
    this.dropdownConfigRequestCompleted = false;
    this.clientList = [];
    this.llmModels = [];
    this.ocrModes = [];
    this.tokenLimits = [...this.fallbackTokenLimits];
    this.dropdownDefaults = { llmModel: '', ocrMode: '', llmTokenLimit: '' };
    this.cdr.markForCheck();

    const clientNames = this.readAssignedClientNamesFromSession();
    this.clientList = clientNames;
    if (clientNames.length === 0) {
      notifyCreateLoadError();
    }
    this.clientsRequestCompleted = true;
    this.updateCreateModalLoadingState();

    this.documentModelService.getAdminDropdownConfig()
      .pipe(
        timeout(8000),
        catchError(() => {
          this.llmModels = [];
          this.ocrModes = [];
          this.tokenLimits = [...this.fallbackTokenLimits];
          this.dropdownDefaults = { llmModel: '', ocrMode: '', llmTokenLimit: '' };
          notifyCreateLoadError();
          return of<AdminDropdownConfigResponse | null>(null);
        })
      )
      .subscribe({
        next: (dropdownConfig) => {
          if (dropdownConfig) {
            this.applyDropdownConfig(dropdownConfig);

            const noLlmModels = this.llmModels.length === 0;
            const noOcrModes = this.ocrModes.length === 0;
            if (noLlmModels || noOcrModes) {
              notifyCreateLoadError();
            }
          }
        },
        complete: () => {
          this.dropdownConfigRequestCompleted = true;
          this.updateCreateModalLoadingState();
        }
      });
  }

  private updateCreateModalLoadingState(): void {
    this.isCreateModalLoading = !(this.clientsRequestCompleted && this.dropdownConfigRequestCompleted);
    this.cdr.markForCheck();
  }

  private readAssignedClientNamesFromSession(): string[] {
    try {
      const raw = sessionStorage.getItem('assignedClients');
      if (!raw) { return []; }
      const parsed = JSON.parse(raw) as Array<{ client_id?: string; client_name?: string }>;
      if (!Array.isArray(parsed)) { return []; }
      const names = parsed
        .map((c) => (c && typeof c.client_name === 'string' ? c.client_name.trim() : ''))
        .filter((n) => n.length > 0);
      return Array.from(new Set(names));
    } catch {
      return [];
    }
  }

  loadSequencingDocumentTypes(): void {
    if (this.isSequencingDocumentTypesLoading) { return; }

    this.isSequencingDocumentTypesLoading = true;
    this.cdr.markForCheck();

    this.sequencingDocumentTypesSub?.unsubscribe();
    this.sequencingDocumentTypesSub = this.truthManagementService
      .getSequencingDocumentTypes({ module: this.authService.getCurrentClientModule() })
      .pipe(timeout(8000))
      .subscribe({
        next: (items) => {
          const options: DocumentTypeOption[] = [];
          const seen = new Set<string>();
          for (const item of (items ?? [])) {
            const code = typeof item?.document_type_code === 'string' ? item.document_type_code.trim() : '';
            const rawName = typeof item?.document_type === 'string' ? item.document_type.trim() : '';
            const name = rawName.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
            if (!code || !name || seen.has(code)) { continue; }
            seen.add(code);
            options.push({ code, name });
          }
          this.sequencingDocumentTypes = options;
          // If either form currently holds a name (from an older Edit GET response),
          // swap it to the corresponding code now that we know the mapping.
          this.createModalForm.documentType = this.resolveDocumentTypeCode(this.createModalForm.documentType);
          this.editModalForm.documentType = this.resolveDocumentTypeCode(this.editModalForm.documentType);
        },
        error: () => {
          this.isSequencingDocumentTypesLoading = false;
          this.showTechnicalError('Failed to load document types');
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isSequencingDocumentTypesLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  private resolveDocumentTypeCode(value: string): string {
    if (!value) { return value; }
    if (this.sequencingDocumentTypes.some((opt) => opt.code === value)) { return value; }
    const byName = this.sequencingDocumentTypes.find((opt) => opt.name === value);
    return byName ? byName.code : value;
  }

  getDocumentTypeName(code: string): string {
    if (!code) { return ''; }
    const match = this.sequencingDocumentTypes.find((opt) => opt.code === code);
    return match ? match.name : '';
  }

  toggleCreateDocTypeDropdown(): void {
    this.isCreateDocTypeDropdownOpen = !this.isCreateDocTypeDropdownOpen;
    this.isEditDocTypeDropdownOpen = false;
    if (this.isCreateDocTypeDropdownOpen) { this.createDocTypeSearch = ''; }
  }

  toggleEditDocTypeDropdown(): void {
    if (this.authService.isReadOnly) { return; }
    this.isEditDocTypeDropdownOpen = !this.isEditDocTypeDropdownOpen;
    this.isCreateDocTypeDropdownOpen = false;
    if (this.isEditDocTypeDropdownOpen) { this.editDocTypeSearch = ''; }
  }

  selectCreateDocType(option: DocumentTypeOption): void {
    this.createModalForm.documentType = option.code;
    this.isCreateDocTypeDropdownOpen = false;
    this.createDocTypeSearch = '';
  }

  selectEditDocType(option: DocumentTypeOption): void {
    this.editModalForm.documentType = option.code;
    this.isEditDocTypeDropdownOpen = false;
    this.editDocTypeSearch = '';
  }

  getFilteredCreateDocTypes(): DocumentTypeOption[] {
    const query = this.createDocTypeSearch.trim().toLowerCase();
    if (!query) { return this.sequencingDocumentTypes; }
    return this.sequencingDocumentTypes.filter(
      (opt) => opt.name.toLowerCase().includes(query) || opt.code.toLowerCase().includes(query),
    );
  }

  getFilteredEditDocTypes(): DocumentTypeOption[] {
    const query = this.editDocTypeSearch.trim().toLowerCase();
    if (!query) { return this.sequencingDocumentTypes; }
    return this.sequencingDocumentTypes.filter(
      (opt) => opt.name.toLowerCase().includes(query) || opt.code.toLowerCase().includes(query),
    );
  }

  onCreateDocTypeSearchInput(event: Event): void {
    this.createDocTypeSearch = (event.target as HTMLInputElement).value ?? '';
  }

  onEditDocTypeSearchInput(event: Event): void {
    this.editDocTypeSearch = (event.target as HTMLInputElement).value ?? '';
  }

  clearCreateDocTypeSearch(): void { this.createDocTypeSearch = ''; }
  clearEditDocTypeSearch(): void { this.editDocTypeSearch = ''; }

  private applyDropdownConfig(dropdownConfig: AdminDropdownConfigResponse | null | undefined): void {
    const llmModels = Array.isArray(dropdownConfig?.llm_models) ? dropdownConfig?.llm_models : [];
    const ocrModes = Array.isArray(dropdownConfig?.ocr_modes) ? dropdownConfig?.ocr_modes : [];
    const tokens = Array.isArray(dropdownConfig?.tokens)
      ? dropdownConfig.tokens.filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value > 0)
      : [];
    const defaults = dropdownConfig?.defaults && typeof dropdownConfig.defaults === 'object'
      ? dropdownConfig.defaults
      : undefined;

    this.llmModels = llmModels.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    this.ocrModes = ocrModes.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
    this.tokenLimits = tokens.length > 0 ? tokens : [...this.fallbackTokenLimits];

    let llmTokenLimitDefault = '';
    if (typeof defaults?.tokens === 'number' && Number.isFinite(defaults.tokens)) {
      llmTokenLimitDefault = String(defaults.tokens);
    }

    this.dropdownDefaults = {
      llmModel: typeof defaults?.llm_model === 'string' ? defaults.llm_model : '',
      ocrMode: typeof defaults?.ocr_mode === 'string' ? defaults.ocr_mode : '',
      llmTokenLimit: llmTokenLimitDefault
    };
  }

  private buildCreateDocumentPayload(): Record<string, string> {
    const payload: Record<string, string> = {
      client_name: this.createModalForm.clientName.trim(),
      document_model: this.createModalForm.documentModel.trim(),
      document_type_id: this.createModalForm.documentType.trim(),
      description: ''
    };

    if (this.createModalForm.contextType.trim()) {
      payload['prompt_keys'] = JSON.stringify({
        important_inclusions: this.createModalForm.contextType.trim()
      });
    }

    if (this.createModalForm.llmModel.trim()) {
      payload['llm_model'] = this.createModalForm.llmModel.trim();
    }

    if (this.createModalForm.ocrMode.trim()) {
      payload['ocr_mode'] = this.createModalForm.ocrMode.trim();
    }

    if (this.createModalForm.llmTokenLimit.trim()) {
      payload['llm_parameters'] = JSON.stringify({ token_limit: Number(this.createModalForm.llmTokenLimit) });
    }

    const otherParameters: Record<string, any> = this.createModalForm.otherDetails.trim()
      ? JSON.parse(this.createModalForm.otherDetails)
      : {};

    if (this.createModalForm.enableExternalMapping) {
      otherParameters['use_external_mapping'] = true;
    }

    if (Object.keys(otherParameters).length > 0) {
      payload['other_parameters'] = JSON.stringify(otherParameters);
    }

    return payload;
  }

  private getEmptyCreateModalForm(): CreateDocumentModalForm {
    return {
      clientName: '',
      documentModel: '',
      documentType: '',
      contextType: '',
      llmModel: '',
      llmTokenLimit: '',
      ocrMode: '',
      otherDetails: '',
      enableExternalMapping: false
    };
  }

  openEditModal(documentModel: string): void {
    this.isEditModalOpen = true;
    this.isEditSubmitting = false;
    this.isEditModalLoading = true;
    this.editModalError = '';
    this.editingDocumentModel = documentModel;
    this.editModalForm = this.getEmptyEditModalForm();
    this.editModalInitialSnapshot = this.getEditModalSnapshot();
    this.loadEditModalData(documentModel);
    this.loadSequencingDocumentTypes();
    this.cdr.markForCheck();
  }

  closeEditModal(): void {
    if (this.isEditSubmitting) {
      return;
    }

    this.isEditModalOpen = false;
    this.isEditModalLoading = false;
    this.isEditSubmitting = false;
    this.isEditDocTypeDropdownOpen = false;
    this.editModalError = '';
    this.editingDocumentModel = '';
    this.editModalForm = this.getEmptyEditModalForm();
    this.editModalInitialSnapshot = this.getEditModalSnapshot();
    this.cdr.markForCheck();
  }

  requestCloseEditModal(): void {
    if (this.isEditSubmitting) {
      return;
    }

    if (!this.hasUnsavedEditModalChanges) {
      this.closeEditModal();
      return;
    }

    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Unsaved Changes',
      message: 'Do you want to save changes?',
      confirmText: 'Yes',
      cancelText: 'No',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onConfirm: () => this.performEditDocumentModelSave(),
      onCancel: () => this.closeEditModal()
    });
  }

  onEditModalBackdropClick(): void {
    return;
  }

  get hasUnsavedEditModalChanges(): boolean {
    return this.getEditModalSnapshot() !== this.editModalInitialSnapshot;
  }

  get isEditOtherDetailsJsonInvalid(): boolean {
    if (!this.editModalForm.otherDetails.trim()) {
      return false;
    }

    try {
      const parsed = JSON.parse(this.editModalForm.otherDetails);
      return typeof parsed !== 'object' || parsed === null || Array.isArray(parsed);
    } catch {
      return true;
    }
  }

  get isEditSaveDisabled(): boolean {
    return this.isEditSubmitting || this.isEditModalLoading || this.isEditOtherDetailsJsonInvalid || !this.hasUnsavedEditModalChanges;
  }

  requestSaveEditDocumentModel(): void {
    if (this.isEditSaveDisabled) {
      return;
    }

    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Unsaved Changes',
      message: 'Do you want to save changes?',
      confirmText: 'Yes',
      cancelText: 'No',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onConfirm: () => this.performEditDocumentModelSave(),
      onCancel: () => this.cdr.markForCheck()
    });
  }

  private performEditDocumentModelSave(): void {
    if (!this.editingDocumentModel.trim()) {
      this.notificationService.showError('Document model is missing. Please retry from the table.');
      return;
    }

    if (this.isEditOtherDetailsJsonInvalid) {
      this.notificationService.showError('Invalid JSON format in Other Details.');
      return;
    }

    if (this.isEditModalLoading) {
      this.notificationService.showError('Model settings are still loading. Please wait.');
      return;
    }

    this.isEditSubmitting = true;
    this.editModalError = '';
    this.cdr.markForCheck();

    const payload = this.buildEditDocumentPayload();
    this.documentModelService.updateAdminDocumentMetadata(this.editingDocumentModel, payload).subscribe({
      next: () => {
        this.notificationService.showSuccess('Document model updated successfully.');
        this.isEditSubmitting = false;
        this.closeEditModal();
        this.loadDocumentModels(true);
      },
      error: () => {
        this.isEditSubmitting = false;
        this.editModalError = '';
        this.showTechnicalError('Failed to update document model');
        this.cdr.markForCheck();
      }
    });
  }

  private loadEditModalData(documentModel: string): void {
    let editDropdownLoadErrorNotified = false;
    const notifyEditDropdownLoadError = () => {
      if (editDropdownLoadErrorNotified) {
        return;
      }
      editDropdownLoadErrorNotified = true;
      this.showTechnicalError('Failed to load dropdowns (OCR Mode, LLM Model)');
    };

    this.editConfigRequestCompleted = false;
    this.editDropdownRequestCompleted = false;
    this.editModalError = '';
    this.isEditModalLoading = true;
    this.cdr.markForCheck();

    this.documentModelService.getAdminDropdownConfig()
      .pipe(
        timeout(8000),
        catchError(() => {
          this.llmModels = [];
          this.ocrModes = [];
          this.tokenLimits = [...this.fallbackTokenLimits];
          this.dropdownDefaults = { llmModel: '', ocrMode: '', llmTokenLimit: '' };
          notifyEditDropdownLoadError();
          return of<AdminDropdownConfigResponse | null>(null);
        })
      )
      .subscribe({
        next: (dropdownConfig) => {
          if (dropdownConfig) {
            this.applyDropdownConfig(dropdownConfig);

            const noLlmModels = this.llmModels.length === 0;
            const noOcrModes = this.ocrModes.length === 0;
            if (noLlmModels || noOcrModes) {
              notifyEditDropdownLoadError();
            }
          }
        },
        complete: () => {
          this.editDropdownRequestCompleted = true;
          this.updateEditModalLoadingState();
        }
      });

    this.documentModelService.getDocumentModelConfig(documentModel)
      .pipe(
        timeout(8000),
        catchError(() => {
          this.showTechnicalError('Failed to load existing document model settings');
          return of<any>(null);
        })
      )
      .subscribe({
        next: (config) => {
          if (config) {
            this.applyEditModalConfig(config);
          }
        },
        complete: () => {
          this.editConfigRequestCompleted = true;
          this.updateEditModalLoadingState();
        }
      });
  }

  private updateEditModalLoadingState(): void {
    this.isEditModalLoading = !(this.editConfigRequestCompleted && this.editDropdownRequestCompleted);
    this.cdr.markForCheck();
  }

  private applyEditModalConfig(config: any): void {
    const promptKeys = this.parseMaybeJson(config?.prompt_keys);
    const llmParameters = this.parseMaybeJson(config?.llm_parameters);
    const ocrParameters = this.parseMaybeJson(config?.ocr_parameters);
    const otherParametersRaw = this.parseMaybeJson(config?.other_parameters);

    let contextType = '';
    if (promptKeys && typeof promptKeys === 'object' && !Array.isArray(promptKeys)) {
      const importantInclusions = (promptKeys as Record<string, any>)['important_inclusions'];
      if (typeof importantInclusions === 'string') {
        contextType = importantInclusions;
      } else if (Array.isArray(importantInclusions)) {
        contextType = importantInclusions.filter((item): item is string => typeof item === 'string').join(', ');
      }
    }

    let llmTokenLimit = '';
    if (llmParameters && typeof llmParameters === 'object' && !Array.isArray(llmParameters)) {
      const tokenLimit = (llmParameters as Record<string, any>)['token_limit'];
      if (typeof tokenLimit === 'number' || typeof tokenLimit === 'string') {
        llmTokenLimit = String(tokenLimit);
      }
    }

    let ocrMode = '';
    if (typeof config?.ocr_mode === 'string' && config.ocr_mode.trim()) {
      ocrMode = config.ocr_mode;
    } else if (ocrParameters && typeof ocrParameters === 'object' && !Array.isArray(ocrParameters)) {
      const ocrModeFromParameters = (ocrParameters as Record<string, any>)['ocr_mode'];
      if (typeof ocrModeFromParameters === 'string') {
        ocrMode = ocrModeFromParameters;
      }
    }

    let otherDetails = '';
    let enableExternalMapping = false;
    if (otherParametersRaw && typeof otherParametersRaw === 'object' && !Array.isArray(otherParametersRaw)) {
      const otherParameters = { ...(otherParametersRaw as Record<string, any>) };
      enableExternalMapping = Boolean(otherParameters['use_external_mapping']);
      delete otherParameters['use_external_mapping'];

      if (Object.keys(otherParameters).length > 0) {
        otherDetails = JSON.stringify(otherParameters, null, 2);
      }
    }

    const rawDocumentType = (config as Record<string, unknown> | null | undefined)?.['document_type_id']
      ?? (config as Record<string, unknown> | null | undefined)?.['document_type'];
    const documentType = typeof rawDocumentType === 'string' ? rawDocumentType.trim() : '';

    this.editModalForm = {
      clientName: typeof config?.client_name === 'string' ? config.client_name : '',
      documentModel: typeof config?.document_model === 'string' ? config.document_model : this.editingDocumentModel,
      documentType,
      contextType,
      llmModel: typeof config?.llm_model === 'string' ? config.llm_model : '',
      llmTokenLimit,
      ocrMode,
      otherDetails,
      enableExternalMapping
    };
    this.editModalInitialSnapshot = this.getEditModalSnapshot();
  }

  private parseMaybeJson(value: any): any {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }

      try {
        return JSON.parse(trimmed);
      } catch {
        return null;
      }
    }

    return value;
  }

  private getEditModalSnapshot(): string {
    return JSON.stringify(this.editModalForm);
  }

  private buildEditDocumentPayload(): Record<string, any> {
    const payload: Record<string, any> = {};

    if (this.editModalForm.documentType.trim()) {
      payload['document_type_id'] = this.editModalForm.documentType.trim();
    }

    if (this.editModalForm.contextType.trim()) {
      payload['prompt_keys'] = {
        important_inclusions: this.editModalForm.contextType.trim()
      };
    }

    if (this.editModalForm.llmModel.trim()) {
      payload['llm_model'] = this.editModalForm.llmModel.trim();
    }

    if (this.editModalForm.llmTokenLimit.trim()) {
      payload['llm_parameters'] = { token_limit: Number(this.editModalForm.llmTokenLimit) };
    }

    if (this.editModalForm.ocrMode.trim()) {
      payload['ocr_mode'] = this.editModalForm.ocrMode.trim();
    }

    const otherParameters: Record<string, any> = this.editModalForm.otherDetails.trim()
      ? JSON.parse(this.editModalForm.otherDetails)
      : {};

    if (this.editModalForm.enableExternalMapping) {
      otherParameters['use_external_mapping'] = true;
    }

    if (Object.keys(otherParameters).length > 0) {
      payload['other_parameters'] = otherParameters;
    }

    return payload;
  }

  private getEmptyEditModalForm(): EditDocumentModalForm {
    return {
      clientName: '',
      documentModel: '',
      documentType: '',
      contextType: '',
      llmModel: '',
      llmTokenLimit: '',
      ocrMode: '',
      otherDetails: '',
      enableExternalMapping: false
    };
  }

  viewFields(documentModel: string, event?: MouseEvent): void {
    if (this.expandedModel === documentModel) {
      this.expandedModel = null;
      this.expandedFields = [];
      this.cdr.markForCheck();
      return;
    }

    // Position dropdown below the clicked element
    if (event) {
      const el = (event.target as HTMLElement).closest('button') || (event.target as HTMLElement);
      const rect = el.getBoundingClientRect();
      this.dropdownTop = rect.bottom + 4;
      this.dropdownLeft = rect.left - 200;
      if (this.dropdownLeft < 8) this.dropdownLeft = 8;
    }

    this.expandedModel = documentModel;
    this.expandedFields = [];
    this.loadingFields = true;
    this.cdr.markForCheck();

    this.documentModelService.getDocumentKeys(documentModel).subscribe({
      next: (response: any) => {
        let fields: string[] = [];

        if (response?.extraction_schema?.properties) {
          // extraction_schema.properties format: keys are the field names
          fields = Object.keys(response.extraction_schema.properties);
        } else if (response?.keys && Array.isArray(response.keys)) {
          fields = response.keys.map((k: any) =>
            typeof k === 'string' ? k : (k.key_label || k.field_name || k.key_name || k.key || '')
          );
        } else if (Array.isArray(response)) {
          fields = response.map((k: any) =>
            typeof k === 'string' ? k : (k.key_label || k.field_name || k.key_name || k.key || '')
          );
        }

        this.expandedFields = fields.filter(f => !!f).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        this.loadingFields = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.expandedFields = [];
        this.loadingFields = false;
        this.cdr.markForCheck();
      }
    });
  }

  editFields(documentModel: string): void {
    this.router.navigate(['/edit-document-model', documentModel]);
  }

  openPromptTuning(documentModel: string): void {
    this.router.navigate(['/prompt-tuning', documentModel], {
      queryParams: { source: 'admin-settings-prompt-tuning' }
    });
  }

  sortBy(column: 'name' | 'fields' | 'modifiedDate' | 'modifiedBy'): void {
    this.sortActive = true;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.cdr.markForCheck();
  }

  getSortIcon(column: string): SafeHtml {
    if (!this.sortActive) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>▲</span><span>▼</span></div>');
    }
    if (this.sortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>▲</span><span>▼</span></div>');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.sortDirection === 'asc' ? '▲' : '▼');
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToEvaluationDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

  /**
   * Generate filename with document model name and timestamp
   * Format: {documentModel}_{timestamp}.{extension}
   * Example: appraisal_report_1004_20260526_143022.json
   */
  private generateFilename(documentModel: string, extension: string): string {
    const now = new Date();
    const timestamp = now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') + '_' +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    
    return `${documentModel}_${timestamp}.${extension}`;
  }

  /**
   * Download schema with format selection modal
   */
  downloadSchema(documentModel: string): void {
    if (this.downloadingModel) {
      return; // Prevent concurrent downloads
    }

    // Show format selection modal as vertical list
    this.modalService.show({
      type: ModalType.CONFIRMATION,
      size: ModalSize.SMALL,
      title: 'Download Schema',
      message: 'Select format:',
      confirmText: 'JSON',
      cancelText: 'Excel',
      isListLayout: true,  
      onConfirm: () => this.downloadAsJson(documentModel),
      onCancel: () => this.downloadAsExcel(documentModel),
      allowEscapeKey: true,
      allowOutsideClick: true
    });
  }

  /**
   * Download schema as JSON file
   */
  downloadAsJson(documentModel: string): void {
    this.downloadingModel = documentModel;
    this.cdr.markForCheck();

    this.documentModelService.getDocumentModel(documentModel).subscribe({
      next: (blob: Blob) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.generateFilename(documentModel, 'json');
        link.click();
        window.URL.revokeObjectURL(url);
        this.downloadingModel = null;
        this.cdr.markForCheck();
      },
      error: (error) => {
        let errorMessage = 'Failed to download schema';
        if (error.status === 404) {
          errorMessage = `Document model '${documentModel}' not found`;
        } else if (error.status === 400) {
          errorMessage = error.error?.message || 'Invalid request';
        } else if (!navigator.onLine) {
          errorMessage = 'Failed to connect. Please check your connection.';
        }
        this.notificationService.showError(errorMessage);
        this.downloadingModel = null;
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Download schema as Excel file
   */
  downloadAsExcel(documentModel: string): void {
    this.downloadingModel = documentModel;
    this.cdr.markForCheck();

    this.documentModelService.getDocumentModel(documentModel).subscribe({
      next: async (blob: Blob) => {
        try {
          // Parse JSON blob
          const jsonText = await blob.text();
          const documentData = JSON.parse(jsonText);

          // Validate extraction_schema exists
          if (!documentData.extraction_schema || !documentData.extraction_schema.properties) {
            throw new Error('Invalid schema structure: missing extraction_schema.properties');
          }

          // Convert to Excel using ExcelExportService
          const filename = this.generateFilename(documentModel, 'xlsx');
          this.excelExportService.convertExtractionSchemaToExcel(
            documentModel,
            documentData.extraction_schema,
            filename
          ).subscribe({
            next: () => {
              this.downloadingModel = null;
              this.cdr.markForCheck();
            },
            error: (error) => {
              this.notificationService.showError('Failed to convert schema to Excel');
              this.downloadingModel = null;
              this.cdr.markForCheck();
            }
          });
        } catch (error) {
          this.notificationService.showError('Failed to parse schema data');
          this.downloadingModel = null;
          this.cdr.markForCheck();
        }
      },
      error: (error) => {
        let errorMessage = 'Failed to download schema';
        if (error.status === 404) {
          errorMessage = `Document model '${documentModel}' not found`;
        } else if (error.status === 400) {
          errorMessage = error.error?.message || 'Invalid request';
        } else if (!navigator.onLine) {
          errorMessage = 'Failed to connect. Please check your connection.';
        }
        this.notificationService.showError(errorMessage);
        this.downloadingModel = null;
        this.cdr.markForCheck();
      }
    });
  }

  openCompareModal(documentModel: string): void {
    this.compareModel = documentModel;
    this.compareTargetEnv = '';
    this.resetCompareResult();
    this.isCompareModalOpen = true;
    this.cdr.markForCheck();
  }

  /** This UI's own environment, parsed from its API host (msuite-llm-de-<dev|qa|uat|app>). */
  private static getCurrentEnv(): string {
    const hostToken = /msuite-llm-de-(dev|qa|uat|app)\./.exec(environment.apiBaseUrl)?.[1];
    return hostToken === 'app' ? 'prod' : (hostToken ?? '');
  }

  /**
   * Target environments visible from this UI's current environment: every lower
   * environment plus the next higher one (excluding the current environment itself).
   * E.g. dev -> [qa], qa -> [dev, uat], uat -> [dev, qa, prod], prod -> [dev, qa, uat].
   */
  private static getOtherEnvs(): string[] {
    const currentEnv = AdminSettingsComponent.getCurrentEnv();
    const currentIndex = AdminSettingsComponent.ENV_HIERARCHY.indexOf(currentEnv);
    if (currentIndex === -1) {
      return [];
    }
    const visibleCount = Math.min(currentIndex + 2, AdminSettingsComponent.ENV_HIERARCHY.length);
    return AdminSettingsComponent.ENV_HIERARCHY.slice(0, visibleCount).filter((env) => env !== currentEnv);
  }

  openPushDialog(): void {
    this.pushTargetEnv = this.compareTargetEnv;
    this.isPushDialogOpen = true;
    this.cdr.markForCheck();
  }

  closePushDialog(): void {
    if (this.isPushing) {
      return;
    }
    this.isPushDialogOpen = false;
    this.cdr.markForCheck();
  }

  runPush(): void {
    if (!this.pushTargetEnv || this.isPushing) {
      return;
    }

    this.isPushing = true;
    this.cdr.markForCheck();

    const wasTargetNotFound = this.compareResult?.status === 'TARGET_NOT_FOUND';

    this.documentModelService.pushDocumentModel(this.compareModel, this.pushTargetEnv).subscribe({
      next: () => {
        const verb = wasTargetNotFound ? 'created in' : 'pushed to';
        this.notificationService.showSuccess(
          `'${this.compareModel}' ${verb} ${this.pushTargetEnv.toUpperCase()} successfully`
        );
        this.isPushing = false;
        this.isPushDialogOpen = false;
        // Re-compare against the pushed environment so the modal shows the post-push state
        this.compareTargetEnv = this.pushTargetEnv;
        this.runCompare();
      },
      error: (error) => {
        const isForbidden = error?.status === 403;
        const message = isForbidden
          ? "You don't have permission to perform this action. Contact admin."
          : error?.error?.message || error?.error?.detail || error?.error?.error
            || (!navigator.onLine ? 'Failed to connect. Please check your connection.' : 'Failed to push document model');
        this.notificationService.showError(message);
        this.isPushing = false;
        if (isForbidden) {
          this.isPushDialogOpen = false;
        }
        this.cdr.markForCheck();
      }
    });
  }

  openPullDialog(): void {
    this.pullTargetEnv = this.compareTargetEnv;
    this.isPullDialogOpen = true;
    this.cdr.markForCheck();
  }

  closePullDialog(): void {
    if (this.isPulling) {
      return;
    }
    this.isPullDialogOpen = false;
    this.cdr.markForCheck();
  }

  runPull(): void {
    if (!this.pullTargetEnv || this.isPulling) {
      return;
    }

    this.isPulling = true;
    this.cdr.markForCheck();

    this.documentModelService.pullDocumentModel(this.compareModel, this.pullTargetEnv).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          `'${this.compareModel}' pulled from ${this.pullTargetEnv.toUpperCase()} successfully`
        );
        this.isPulling = false;
        this.isPullDialogOpen = false;
        // Pull rewrote this environment, so the list behind the modal is now stale
        this.loadDocumentModels(true);
        // Re-compare against the source environment so the modal shows the post-pull state
        this.compareTargetEnv = this.pullTargetEnv;
        this.runCompare();
      },
      error: (error) => {
        const isForbidden = error?.status === 403;
        const message = isForbidden
          ? "You don't have permission to perform this action. Contact admin."
          : error?.error?.message || error?.error?.detail || error?.error?.error
            || (!navigator.onLine ? 'Failed to connect. Please check your connection.' : 'Failed to pull document model');
        this.notificationService.showError(message);
        this.isPulling = false;
        if (isForbidden) {
          this.isPullDialogOpen = false;
        }
        this.cdr.markForCheck();
      }
    });
  }

  closeCompareModal(): void {
    if (this.isPushing || this.isPulling) {
      return;
    }
    this.isPushDialogOpen = false;
    this.isPullDialogOpen = false;
    this.isCompareModalOpen = false;
    this.compareModel = '';
    this.compareTargetEnv = '';
    this.resetCompareResult();
    this.cdr.markForCheck();
  }

  onCompareTargetChange(): void {
    this.resetCompareResult();
  }

  runCompare(): void {
    if (!this.compareTargetEnv || this.isComparing) {
      return;
    }

    this.isComparing = true;
    this.resetCompareResult();
    this.cdr.markForCheck();

    this.documentModelService.compareDocumentModel(this.compareModel, this.compareTargetEnv).subscribe({
      next: (result) => {
        const diffPaths = new Set(result.differences.map((d) => d.path));
        this.compareResult = result;
        this.compareCurrentLines = this.toCompareLines(result.current, diffPaths);
        this.compareTargetLines = result.target ? this.toCompareLines(result.target, diffPaths) : [];
        this.isComparing = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        const isForbidden = error?.status === 403;
        this.isComparing = false;
        if (isForbidden) {
          this.notificationService.showError("You don't have permission to perform this action. Contact admin.");
          this.closeCompareModal();
          return;
        }
        this.compareError = error?.error?.message || error?.error?.error
          || (!navigator.onLine ? 'Failed to connect. Please check your connection.' : 'Failed to compare document model');
        this.cdr.markForCheck();
      }
    });
  }

  formatCompareValue(value: unknown): string {
    return value === undefined ? '(missing)' : JSON.stringify(value);
  }

  /** Break a dotted diff path (e.g. "extraction_schema.properties.borrower_name.description") into a human-readable breadcrumb. */
  formatDiffPath(path: string): string[] {
    const skipSegments = new Set(['properties']);
    const wordOverrides: Record<string, string> = { llm: 'LLM', ocr: 'OCR', id: 'ID' };

    // For field diffs, the schema-root/category prefix ("Extraction Schema", "Computed Fields" ›
    // "Aggregations", etc.) is confusing noise — jump-navigation and the highlighter already show
    // where the diff lives, so start the breadcrumb at the field name instead.
    const segments = path.split('.');
    const relevantSegments = this.isFieldDiffPath(path) ? segments.slice(2) : segments;

    return relevantSegments
      .filter((segment) => segment && !skipSegments.has(segment))
      .map((segment) =>
        segment
          .split('_')
          .map((word) => wordOverrides[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      );
  }

  // Both prefixes put the field name at segment index 2:
  // "extraction_schema.properties.<field>...", "computed_fields.<section>.<field>..."
  private readonly fieldPathPrefixes = ['extraction_schema.properties.', 'computed_fields.'];

  /** Diffs that belong to a specific extraction schema or computed schema field. */
  getFieldDiffs(diffs: DocumentCompareDifference[]): DocumentCompareDifference[] {
    return this.expandDiffs(diffs).filter((diff) => this.isFieldDiffPath(diff.path));
  }

  /** Diffs that are not tied to one specific field — everything else goes under Configuration. */
  getConfigDiffs(diffs: DocumentCompareDifference[]): DocumentCompareDifference[] {
    return this.expandDiffs(diffs).filter((diff) => !this.isFieldDiffPath(diff.path));
  }

  /** True only when the path reaches an actual field name under extraction_schema or computed_fields. */
  private isFieldDiffPath(path: string): boolean {
    const segments = path.split('.');
    return segments.length > 2 && this.fieldPathPrefixes.some((prefix) => path.startsWith(prefix));
  }

  /**
   * Backend diffs stop descending as soon as one side isn't an object (e.g. a whole
   * "aggregations" map is missing on one side), bundling every field under it into a single
   * row. Re-expand those down to per-field/per-leaf rows so each field still lists separately
   * under the correct Fields/Configuration group.
   */
  private expandDiffs(diffs: DocumentCompareDifference[]): DocumentCompareDifference[] {
    return diffs.flatMap((diff) => this.diffValues(diff.current, diff.target, diff.path));
  }

  private diffValues(current: unknown, target: unknown, path: string): DocumentCompareDifference[] {
    const currentIsObject = this.isPlainObject(current);
    const targetIsObject = this.isPlainObject(target);
    if (currentIsObject || targetIsObject) {
      const currentObj = currentIsObject ? (current as Record<string, unknown>) : {};
      const targetObj = targetIsObject ? (target as Record<string, unknown>) : {};
      const keys = new Set([...Object.keys(currentObj), ...Object.keys(targetObj)]);
      return [...keys].flatMap((key) =>
        this.diffValues(currentObj[key], targetObj[key], path ? `${path}.${key}` : key)
      );
    }
    return JSON.stringify(current) === JSON.stringify(target) ? [] : [{ path, current, target }];
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  /** Scroll both JSON panels to the line matching this diff path. */
  scrollToPath(path: string): void {
    this.scrollPanelToPath(this.currentJsonPanel, this.compareCurrentLines, path);
    this.scrollPanelToPath(this.targetJsonPanel, this.compareTargetLines, path);
  }

  private scrollPanelToPath(
    panelRef: ElementRef<HTMLElement> | undefined,
    lines: CompareJsonLine[],
    path: string
  ): void {
    const index = this.findClosestLineIndex(lines, path);
    const lineEl = index >= 0 ? (panelRef?.nativeElement.children[index] as HTMLElement | undefined) : undefined;
    if (!lineEl) {
      return;
    }
    lineEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  /** Find the line for this exact path, falling back to the nearest ancestor path present in this panel (e.g. a field missing on one side). */
  private findClosestLineIndex(lines: CompareJsonLine[], path: string): number {
    let candidate = path;
    while (candidate) {
      const index = lines.findIndex((line) => line.path === candidate);
      if (index >= 0) {
        return index;
      }
      const lastDot = candidate.lastIndexOf('.');
      candidate = lastDot >= 0 ? candidate.slice(0, lastDot) : '';
    }
    return -1;
  }

  private resetCompareResult(): void {
    this.compareError = '';
    this.compareResult = null;
    this.compareCurrentLines = [];
    this.compareTargetLines = [];
  }

  /** Pretty-print a value as lines, flagging every line that belongs to a differing path. */
  private toCompareLines(
    value: unknown,
    diffPaths: Set<string>,
    path: string = '',
    indent: number = 0,
    keyPrefix: string = '',
    parentChanged: boolean = false
  ): CompareJsonLine[] {
    const pad = '  '.repeat(indent);
    const changed = parentChanged || diffPaths.has(path);

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length === 0) {
        return [{ text: `${pad}${keyPrefix}{}`, changed, path }];
      }
      const lines: CompareJsonLine[] = [{ text: `${pad}${keyPrefix}{`, changed, path }];
      entries.forEach(([key, child], index) => {
        const childLines = this.toCompareLines(
          child, diffPaths, path ? `${path}.${key}` : key, indent + 1, `${JSON.stringify(key)}: `, changed
        );
        if (index < entries.length - 1) {
          childLines[childLines.length - 1].text += ',';
        }
        lines.push(...childLines);
      });
      lines.push({ text: `${pad}}`, changed, path });
      return lines;
    }

    // Primitives and arrays: render as (possibly multi-line) JSON; arrays are diffed as a whole.
    const rendered = JSON.stringify(value, null, 2) ?? 'null';
    return rendered.split('\n').map((line, i) => ({
      text: i === 0 ? `${pad}${keyPrefix}${line}` : `${pad}${line}`,
      changed,
      path
    }));
  }

  /**
   * Trigger file input for schema import
   */
  importSchema(documentModel: string): void {
    if (this.importingModel) {
      return; // Prevent concurrent imports
    }

    // Trigger file input (no loading state yet)
    if (this.fileInput) {
      this.fileInput.nativeElement.value = ''; // Reset input
      this.fileInput.nativeElement.setAttribute('data-document-model', documentModel);
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * Handle file selection for import
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const documentModel = input.getAttribute('data-document-model');
    
    if (!file || !documentModel) {
      return;
    }

    // Set loading state now
    this.importingModel = documentModel;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.json')) {
      this.notificationService.showError('Invalid file type. Please select a JSON file.');
      this.importingModel = null;
      this.cdr.markForCheck();
      return;
    }

    // Validate MIME type
    if (file.type && file.type !== 'application/json') {
      this.notificationService.showError('Invalid file type. Please select a JSON file.');
      this.importingModel = null;
      this.cdr.markForCheck();
      return;
    }

    // Read and validate JSON structure
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        JSON.parse(e.target?.result as string);
        // If parsing succeeds, upload immediately — backend validates structure
        this.uploadSchemaFile(documentModel, file);
      } catch (error) {
        this.notificationService.showError('Invalid JSON file. Please check the file format.');
        this.importingModel = null;
        this.cdr.markForCheck();
      }
    };
    reader.readAsText(file);
  }

  /**
   * Upload schema file to server
   */
  private uploadSchemaFile(documentModel: string, file: File): void {
    this.documentModelService.updateDocumentModel(documentModel, file).subscribe({
      next: (response) => {
        this.notificationService.showSuccess(`Schema for '${documentModel}' updated successfully`);
        this.importingModel = null;

        // Reload document models table (force-refresh so field_count/last_modified reflect the new schema)
        this.loadDocumentModels(true);
        // Bust the cached document-keys and schema for this model so Truth
        // Management fetches fresh data on the next visit.
        this.truthManagementService.invalidateDocumentModelCache(documentModel);
        this.cdr.markForCheck();
      },
      error: (error) => {
      // Extract error message from backend response
      const backendMessage = error.error?.message || error.error?.detail;
      
      // Only handle connectivity issues — all business logic validation comes from backend
      let errorMessage = backendMessage || 'Failed to import schema';
      if (!navigator.onLine) {
        errorMessage = 'Failed to connect. Please check your connection.';
      }
      
      this.notificationService.showError(errorMessage);
      this.importingModel = null;
      this.cdr.markForCheck();
      }
    });
  }
}

